import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faFileInvoice,
  faCircleNotch,
} from "@fortawesome/free-solid-svg-icons";
import { AnimatePresence, motion } from "framer-motion";
import { popIn } from "./motion/presets";
import { getAllQuotes } from "../Api";
import { handleRole } from "../Helper/handleRoleHelper";
import AnimatedLoader from "../motionKit/AnimatedLoader";

/**
 * Command palette / launcher — press Cmd/Ctrl-K to open.
 * - Type to filter app sections (nav).
 * - Type 2+ characters to ALSO live-search quotes & queries on the server
 *   (customer, VANT ref, subject, route, id — the broadened search) and jump
 *   straight into a quote with Enter.
 * Purely additive: renders nothing until opened. Theme-safe (existing slate +
 * fk-accent tokens, dark: variants — no new colors). Search failures degrade
 * silently to nav-only results; the palette never toasts.
 */
const QUOTE_SEARCH_ROLES = new Set(["admin", "superAdmin", "operator"]);

const CommandPalette = ({ items = [] }) => {
  const navigate = useNavigate();
  const loginToken = useSelector((state) => state?.reducer?.userToken);
  const userLoggedIn = useSelector((state) => state?.reducer?.userLoggedIn);
  const role = userLoggedIn?.role;
  const canSearchQuotes = Boolean(loginToken && QUOTE_SEARCH_ROLES.has(role));

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const [quoteResults, setQuoteResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const inputRef = useRef(null);
  const requestSeq = useRef(0);

  // Global Cmd/Ctrl-K toggles the palette.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Reset + focus each time it opens.
  useEffect(() => {
    if (!open) return undefined;
    setQuery("");
    setActive(0);
    setQuoteResults([]);
    setSearching(false);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  // Debounced live quote search (2+ chars). Stale responses are discarded via
  // a sequence counter; failures degrade silently to nav-only results.
  useEffect(() => {
    if (!open || !canSearchQuotes) return undefined;
    const term = query.trim();
    if (term.length < 2) {
      requestSeq.current += 1; // invalidate any in-flight search
      setQuoteResults([]);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    const seq = ++requestSeq.current;
    const t = setTimeout(async () => {
      try {
        const res = await getAllQuotes({
          loginToken,
          role,
          page: 1,
          limit: 6,
          search: term,
          searchOnField: "all",
        });
        if (seq !== requestSeq.current) return; // stale
        setQuoteResults(Array.isArray(res?.quotes) ? res.quotes : []);
      } catch {
        if (seq === requestSeq.current) setQuoteResults([]);
      } finally {
        if (seq === requestSeq.current) setSearching(false);
      }
    }, 250);
    return () => {
      clearTimeout(t);
      // Runs before every re-run and on close/unmount: whatever request the
      // previous body scheduled is now stale (the next body takes a new seq).
      requestSeq.current += 1;
    };
  }, [open, canSearchQuotes, query, loginToken, role]);

  // Flattened result list: nav matches first, then live quote matches.
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    const navList = items
      .filter((it) => it && it.name && it.path)
      .filter((it) => (q ? it.name.toLowerCase().includes(q) : true))
      .map((it) => ({ kind: "nav", key: `nav:${it.path}`, ...it }));
    const quoteList = quoteResults.map((qt) => {
      const customer = qt?.customerDetails?.name || "";
      const title = customer || qt?.subject || "Untitled query";
      const bits = [
        qt?.vantRef,
        customer && qt?.subject ? qt.subject : "",
        qt?.containerDetails?.origin && qt?.containerDetails?.destination
          ? `${qt.containerDetails.origin} → ${qt.containerDetails.destination}`
          : "",
      ].filter(Boolean);
      // Manual-source queries have their own detail page; everything else
      // (email queries + quotes) opens in the quote workspace.
      const detail =
        qt?.source === "manual"
          ? `/${handleRole(role)}/manual-query/${qt._id}`
          : `/${handleRole(role)}/newQuotePage/${qt._id}`;
      return {
        kind: "quote",
        key: `quote:${qt._id}`,
        name: title,
        subtitle: bits.join("  ·  "),
        path: detail,
      };
    });
    return [...navList, ...quoteList];
  }, [items, query, quoteResults, role]);

  useEffect(() => {
    if (active > results.length - 1) setActive(0);
  }, [results.length, active]);

  const go = (item) => {
    if (!item) return;
    setOpen(false);
    navigate(item.path);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
    else if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.max(0, Math.min(a + 1, results.length - 1))); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); go(results[active]); }
  };

  const firstQuoteIndex = results.findIndex((r) => r.kind === "quote");
  const showQuoteSection = firstQuoteIndex !== -1 || searching;

  const sectionHeading = (label) => (
    <li
      aria-hidden
      className="px-4 pb-1 pt-2.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 dark:text-slate-500"
    >
      {label}
    </li>
  );

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-start justify-center px-4 pt-[12vh]"
          role="dialog"
          aria-modal="true"
          aria-label="Command palette"
          onMouseDown={() => setOpen(false)}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12 }}
        >
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm dark:bg-black/60" />
          <motion.div
            className="relative w-full max-w-xl overflow-hidden rounded-2xl border border-slate-200 bg-white/95 shadow-2xl backdrop-blur-xl dark:border-[oklch(0.33_0.022_264)] dark:bg-[oklch(0.225_0.026_264)]/95"
            onMouseDown={(e) => e.stopPropagation()}
            variants={popIn}
            initial="hidden"
            animate="show"
            exit="exit"
          >
        <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3 dark:border-[oklch(0.33_0.022_264)]">
          <FontAwesomeIcon icon={faMagnifyingGlass} className="text-slate-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={
              canSearchQuotes
                ? "Search quotes by customer, VANT ref, route…  or jump to a section"
                : "Jump to…  (type a section)"
            }
            aria-label={canSearchQuotes ? "Search quotes or sections" : "Search sections"}
            className="w-full bg-transparent text-sm text-slate-800 outline-none placeholder:text-slate-400 dark:text-slate-100"
          />
          {searching && (
            <FontAwesomeIcon icon={faCircleNotch} className="animate-spin text-slate-400" />
          )}
          <kbd className="hidden rounded border border-slate-300 px-1.5 py-0.5 text-[10px] text-slate-400 dark:border-[oklch(0.4_0.02_264)] sm:inline">
            esc
          </kbd>
        </div>
        <ul className="max-h-[52vh] overflow-y-auto py-1">
          {results.length === 0 && !searching ? (
            <li className="flex flex-col items-center px-4 py-6 text-center text-sm text-slate-400">
              <AnimatedLoader variant="radar" size={110} label="No matches" />
              <span className="mt-2 block">
                {query.trim().length >= 2 && canSearchQuotes
                  ? "No sections or quotes match"
                  : "No matches"}
              </span>
            </li>
          ) : (
            results.map((it, i) => (
              <React.Fragment key={it.key}>
                {i === 0 && it.kind === "nav" && sectionHeading("Go to")}
                {i === firstQuoteIndex && sectionHeading("Quotes")}
                <li>
                  <button
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(it)}
                    className={`flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                      i === active
                        ? "bg-slate-100 text-fk-accent dark:bg-[oklch(0.26_0.028_264/0.7)]"
                        : "text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-[oklch(0.26_0.028_264/0.4)]"
                    }`}
                  >
                    <span className="flex h-5 w-5 shrink-0 items-center justify-center">
                      {it.kind === "quote" ? (
                        <FontAwesomeIcon
                          icon={faFileInvoice}
                          className="text-[13px] text-slate-400 dark:text-slate-500"
                        />
                      ) : typeof it.icon === "function" ? (
                        it.icon({})
                      ) : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{it.name}</span>
                      {it.subtitle ? (
                        <span className="block truncate text-[11px] text-slate-400 dark:text-slate-500">
                          {it.subtitle}
                        </span>
                      ) : null}
                    </span>
                    {i === active && <span className="text-[11px] text-slate-400">↵</span>}
                  </button>
                </li>
              </React.Fragment>
            ))
          )}
          {searching && firstQuoteIndex === -1 && (
            <>
              {sectionHeading("Quotes")}
              <li className="flex flex-col items-center px-4 pb-3 pt-1 text-center text-xs text-slate-400 dark:text-slate-500">
                <AnimatedLoader variant="radar" size={72} label="Searching quotes" />
                <span className="mt-1 block">Searching quotes…</span>
              </li>
            </>
          )}
        </ul>
        {showQuoteSection && (
          <div className="border-t border-slate-200 px-4 py-2 text-[10.5px] text-slate-400 dark:border-[oklch(0.33_0.022_264)] dark:text-slate-500">
            ↵ opens the quote · searches customer, VANT ref, subject, route & id
          </div>
        )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CommandPalette;
